import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { ErrorNote } from '@/components/ui';
import { farmScopeQuery } from '@/stores/farmScope';
import { usePreferences } from '@/stores/preferences';
import { RUNNING_SHORT, useShellNav, type ScreenIntent } from '@/stores/shellNav';

/** Items whose screen should open narrowed to what the item counted. */
const ACTION_INTENTS: Record<string, ScreenIntent> = {
  'below-threshold': RUNNING_SHORT,
};

/**
 * System-computed items that need a decision — pending approvals, draft
 * documents, below-threshold stock, expiring batches, drafts someone else
 * could publish. Never human-authored: that is what the announcement ticker
 * is for. Only providers the caller holds the permission for ever reach this
 * component; there is nothing to filter here, only to render.
 */
export function ActionNeeded({ farmIds = [] }: { farmIds?: readonly string[] }) {
  const navigate = useShellNav((s) => s.navigate);
  const language = usePreferences((s) => s.prefs.language);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['dashboard', 'action-items', farmIds],
    queryFn: () => api.get('/api/v1/dashboard/action-items', farmScopeQuery(farmIds)),
    refetchInterval: 60_000,
  });

  return (
    <section className="panel actionneeded">
      <h3 className="panel__title">Action needed</h3>

      {isError && <ErrorNote message={error.message} />}

      {isPending ? (
        <p className="muted">Loading…</p>
      ) : data && data.length === 0 ? (
        <p className="muted">Nothing needs your attention.</p>
      ) : (
        <ul className="actionneeded__list">
          {(data ?? []).map((item) => (
            <li
              key={item.key}
              className={`actionneeded__item actionneeded__item--${item.severity}`}
            >
              <span className="actionneeded__icon" aria-hidden="true">
                {item.severity === 'urgent'
                  ? '⚠'
                  : item.severity === 'attention'
                    ? '●'
                    : 'ℹ'}
              </span>
              <div className="actionneeded__body">
                <div className="actionneeded__title">
                  {language === 'ur' ? item.titleUr : item.titleEn}
                  <span className="actionneeded__count">{item.count}</span>
                </div>
                {item.samples.length > 0 && (
                  <p className="dim actionneeded__samples">
                    {item.samples.map((s) => s.label).join(', ')}
                    {item.count > item.samples.length &&
                      ` and ${item.count - item.samples.length} more`}
                  </p>
                )}
              </div>
              <button className="btn" onClick={() => navigate(item.screen, ACTION_INTENTS[item.key])}>
                Open
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
